import type { AnswerKeyGateDocument } from 'components';
import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

import type { DevPlanState } from '../TraineeDashboard/DevelopmentPlan/types';

export type ActivityPlanRef = { id: number; code: string; name: string };

export type SoloSessionItem = {
  type: 'solo_session';
  launch_id: number;
  user_id: number;
  session_id: number;
  node_internal_code: string | null;
  node_name: string;
  node_track: string | null;
  node_track_color: string | null;
  mini_session_name: string;
  blueprint_slug: string;
  rank: number;
  status:
    | 'in_progress'
    | 'passed'
    | 'failed'
    | 'failed_no_participation'
    | 'finished_ungraded'
    | 'deferred'
    | 'not_graded';
  score_pct: number | null;
  pass_bar: number | null;
  /** Share of the conviction-slider surface set — see ConvictionCoverageNote. */
  conviction_coverage?: number | null;
  timestamp: string;
};

/**
 * A candidate's score, and the ANSWER KEY's verdict on itself.
 *
 * It travels with every score on this dashboard for one reason: a key that
 * failed its own quality audit may show its numbers but may not wear a
 * hiring band, and this is the screen on which the hiring decision is taken.
 * Absent means the endpoint did not send it; `null` means there is genuinely
 * no check on record. The two read differently — see components/VerdictBanner.
 *
 * WHAT ARRIVES HERE IS THE OUTCOME, NOT THE EVIDENCE. This screen's readers
 * are a trainer and an external CLIENT OBSERVER, and explanations belong to
 * administrators (ruling, 2026-09-03), so `pm.klasses.activity.outcome_only`
 * sends exactly eight fields:
 *
 *     verdict · blocking · overridden · stale · recorded ·
 *     rating_is_evidence · key_retired · headline
 *
 * `headline` is ONE neutral sentence that names no number, no answer-key code
 * and no check. The stamped document behind it — the pass-bar arithmetic, the
 * failing checks in their own words, the key's code, who overrode it and why
 * (`link_validity_snapshot`, pm/assessment/services/attach_gate.py) — reaches
 * an administrator and nobody else. The type stays the wide one because
 * `readAnswerKeyGate` reads four producers defensively and every field on it
 * is optional; what this endpoint sends is pinned in
 * pm/klasses/tests/test_activity_audience.py.
 */
export type LatestAssessment = {
  session_id: number;
  official_composite: number;
  rating: string;
  rank: number | null;
  /**
   * What the candidate did in the room: `traded` (at least one order or
   * currency exchange), `voted_only` (conviction sliders and/or news
   * assignments, no trade) or `absent` (nothing).
   *
   * It travels with the rating because the score no longer implies a trade:
   * pillar 1 is a third of the official composite and is earned entirely on
   * the conviction and news surfaces. Unlike `validity`, it is an OUTCOME and
   * is therefore the same value for every reader.
   *
   * Absent means the endpoint did not serve it (a score graded before the flag
   * existed); the marker then renders nothing rather than guessing.
   */
  participation?: string | null;
  /**
   * Share (0..1) of the conviction-slider surface the candidate set a view
   * on. Printed beside the score by `ConvictionCoverageNote` when under half.
   */
  conviction_coverage?: number | null;
  computed_at: string;
  validity?: AnswerKeyGateDocument | null;
} | null;

export type OverviewTrainee = {
  user_id: number;
  username: string;
  display_name: string;
  can_see_devplan: boolean;
  sessions: {
    provisioned: number;
    played: number;
    deals: number;
    busted: number;
    auto_traded: number;
    latest_assessment: LatestAssessment;
  };
  devplan: {
    level: number | null;
    total_xp: number;
    coins: number | null;
    nodes_owned: number;
    nodes_total: number;
    rank_sum: number;
    attempts: {
      [kind: string]: { count: number; passed: number };
    };
    ladders_done: number;
    games_played: number;
    solo_sessions: {
      launched: number;
      passed: number;
      failed: number;
      failed_no_participation: number;
      in_progress: number;
      finished_ungraded: number;
      deferred?: number;
      not_graded?: number;
      latest: SoloSessionItem | null;
    };
  };
  last_action: string | null;
};

export type ActivityOverview = {
  klass: { id: number; name: string; code: string };
  plan: ActivityPlanRef | null;
  available_plans: ActivityPlanRef[];
  trainees: OverviewTrainee[];
};

export type TimelineItem = {
  type: string;
  timestamp: string;
  // session_started / session_finished items
  session_id?: number;
  played?: boolean;
  deals?: number;
  busted?: boolean;
  auto_traded?: boolean;
  blueprint?: { name: string; difficulty: string } | null;
  assessment?: {
    official_composite: number;
    rating: string;
    rank: number | null;
    /** What the candidate did in the room — see LatestAssessment. */
    participation?: string | null;
    /** Conviction-slider coverage — see LatestAssessment. */
    conviction_coverage?: number | null;
    computed_at: string;
    /** The answer key's outcome document — see LatestAssessment. */
    validity?: AnswerKeyGateDocument | null;
  } | null;
  // solo + attempt items
  kind?: string;
  node_internal_code?: string | null;
  node_name?: string | null;
  node_track?: string | null;
  node_track_color?: string | null;
  mini_session_name?: string;
  asset_key?: string | null;
  asset_title?: string | null;
  rank?: number | null;
  score_pct?: number | null;
  pass_bar?: number | null;
  conviction_coverage?: number | null;
  passed?: boolean;
  status?: string;
  duration_ms?: number;
};

export type ProgressMapNode = {
  internal_code: string;
  code: string;
  name: string;
  tier: string;
  track: string;
  group: string;
  max_rank: number;
  rank: number;
  owned: boolean;
  unlocked_at: string | null;
  ladders_done: number[];
  scenario_attempts: number;
  scenario_passes: number;
};

export type ActivityDetail = {
  klass: { id: number; name: string; code: string };
  trainee: { user_id: number; username: string; display_name: string };
  timeline: TimelineItem[];
  progress_map: {
    plan: ActivityPlanRef;
    profile: { level: number; total_xp: number; coins: number } | null;
    nodes: ProgressMapNode[];
  } | null;
};

export type MatrixNode = {
  internal_code: string;
  code: string;
  name: string;
  tier: string;
  track: string;
  max_rank: number;
  has_mini_sessions: boolean;
};

export type MatrixCell = {
  rank: number;
  owned: boolean;
  ladders_done: number[];
  scenario_attempts: number;
  scenario_passes: number;
  solo: {
    status: string;
    rank: number;
    mini_session_name: string;
    score_pct: number | null;
    timestamp: string;
  } | null;
};

export type ActivityMatrix = {
  klass: { id: number; name: string; code: string };
  plan: ActivityPlanRef | null;
  nodes: MatrixNode[];
  trainees: { id: number; username: string }[];
  cells: { [userId: string]: { [nodeCode: string]: MatrixCell } };
};

export type ObservedKlass = { id: number; name: string; code: string };

const request = async <Result>(
  path: string,
  params: Record<string, string | number | undefined>,
  fallback: string,
): Promise<Result> => {
  const response = await apiCaller({
    path,
    method: 'GET',
    data: params,
    apiUrl: API_URL,
  });
  if (response.status.toString()[0] !== '2') {
    throw new Error(fallback);
  }
  return response.result as Result;
};

export const fetchObservedKlasses = () =>
  request<ObservedKlass[]>('/klasses', {}, 'Your classes could not be loaded.');

export const fetchActivityOverview = (klassId: number, plan?: string) =>
  request<ActivityOverview>(
    `/klasses/${klassId}/activity_overview`,
    plan ? { plan } : {},
    'The activity overview could not be loaded.',
  );

export const fetchActivityMatrix = (klassId: number, plan?: string) =>
  request<ActivityMatrix>(
    `/klasses/${klassId}/activity_matrix`,
    plan ? { plan } : {},
    'The node matrix could not be loaded.',
  );

export const fetchTraineeActivityDetail = (
  klassId: number,
  userId: number,
  plan?: string,
) =>
  request<ActivityDetail>(
    `/klasses/${klassId}/trainees/${userId}/activity_detail`,
    plan ? { plan } : {},
    'The trainee activity detail could not be loaded.',
  );

export type SessionInspect = {
  session: {
    id: number;
    status: number;
    scenario_type: number;
    blueprint: { name: string; difficulty: string } | null;
    starting_cash: number;
    reporting_currency: { name: string; symbol: string } | null;
    start_date: string | null;
    end_date: string | null;
    create_date: string;
    finish_date: string | null;
    has_benchmark: boolean;
  };
  player: {
    initial_cash: number;
    deals: number;
    busted: boolean;
    auto_traded: boolean;
    played: boolean;
    last_portfolio_return: number;
    last_market_value: number;
    derived_pnl: number;
    traded_asset_classes: string[];
    last_metrics: {
      timestamp: string;
      scenario_date: string | null;
      market_value: number;
      portfolio_return: number;
      daily_portfolio_return: number;
      market_index_return: number;
      portfolio_volatility: number;
      beta: number;
      alpha: number;
      sharpe_ratio: number;
      treynor_ratio: number;
      var05: number;
      peak_return: number;
      mdd: number;
    } | null;
  };
  series: { t: string; player: number; benchmark: number | null }[];
  ai_report: { prompt_name: string; response: string } | null;
};

export const fetchTraineeSessionInspect = (
  klassId: number,
  userId: number,
  sessionId: number,
) =>
  request<SessionInspect>(
    `/klasses/${klassId}/trainees/${userId}/session_inspect`,
    { session_id: sessionId },
    'The session details could not be loaded.',
  );

/** The trainee's full /me-shaped DevPlanState (observer read, mints nothing) —
 *  feeds the real skill-tree canvas in the Studio inspect card. */
export const fetchTraineeDevplanState = (
  klassId: number,
  userId: number,
  plan?: string,
) =>
  request<DevPlanState>(
    `/klasses/${klassId}/trainees/${userId}/devplan_state`,
    plan ? { plan } : {},
    'The development plan could not be loaded.',
  );
